import type { ReactNode } from 'react';
import { FONT_AWESOME_NAMES, FONT_AWESOME_STYLE, fontAwesomeEnabled } from './fontawesome';

/**
 * Registre d'icônes du design system (ADR-092 §2) : SVG inline écrits ici, trait `currentColor`,
 * aucune dépendance ni classe CSS venue de la configuration. La navigation ne porte qu'un **nom**
 * (`navigation-body#/$defs/item/icon`) ; un nom inconnu rend l'icône générique. Avec le kit Font Awesome
 * (ADR-131), le même nom rend l'icône Font Awesome correspondante ; ces SVG restent le repli sans kit.
 */

const PATHS: Readonly<Record<string, ReactNode>> = {
  // Médiathèque (ADR-106).
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 17-5-5-9 8" />
    </>
  ),
  upload: <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  // Icônes des zones de carte (ADR-100).
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  phone: <path d="M5 4h4l2 5-3 2a12 12 0 0 0 5 5l2-3 5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z" />,
  // Téléphone debout, bouton en bas (Font Awesome `mobile-screen-button`) ; le paysage le tourne (`rotate`).
  mobile: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M7 17.5h10M11.5 19.5h1" />
    </>
  ),
  location: (
    <>
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  thermometer: <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z" />,
  droplet: <path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z" />,
  gauge: (
    <>
      <path d="M4 16a8 8 0 1 1 16 0" />
      <path d="m12 16 4-5" />
    </>
  ),
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
      <path d="M10 21v-6h4v6" />
    </>
  ),
  inbox: (
    <>
      <path d="M3 13h5l1.5 3h5L16 13h5" />
      <path d="M5.5 5h13L21 13v6H3v-6z" />
    </>
  ),
  package: (
    <>
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="m4 7.5 8 4.5 8-4.5" />
      <path d="M12 12v9" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1" />
      <path d="M9 11h6M9 15h4" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 9 5-9 5-9-5z" />
      <path d="m3 13 9 5 9-5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7" />
      <path d="M18 14.3c2 .7 3.5 2.8 3.5 5.7" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 16v-4M12 16V8M16 16v-6" />
    </>
  ),
  logout: (
    <>
      <path d="M14 4h5v16h-5" />
      <path d="M10 8l-4 4 4 4" />
      <path d="M6 12h10" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
    </>
  ),
  book: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
      <path d="M4 21V5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.01" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14" />
      <path d="M12 17.5v.01" />
    </>
  ),
  truck: (
    <>
      <path d="M2 6h11v10H2z" />
      <path d="M13 9h4l4 4v3h-8" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </>
  ),
  scale: (
    <>
      <path d="M12 4v16M7 20h10" />
      <path d="M4 8h16" />
      <path d="m6 8-3 6a3 3 0 0 0 6 0zM18 8l-3 6a3 3 0 0 0 6 0z" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2 20h20z" />
      <path d="M12 10v4M12 17v.01" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 5 5" />
    </>
  ),
  chevron: <path d="m9 6 6 6-6 6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-up': <path d="m6 15 6-6 6 6" />,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  tag: (
    <>
      <path d="M3 12V3h9l9 9-9 9z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </>
  ),
  euro: (
    <>
      <path d="M17 6.5A6.5 6.5 0 1 0 17 17.5" />
      <path d="M4 10h9M4 14h9" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5.5" rx="8" ry="3" />
      <path d="M4 5.5v13c0 1.7 3.6 3 8 3s8-1.3 8-3v-13" />
      <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>
  ),
  dot: <circle cx="12" cy="12" r="4" />,
  'external-link': (
    <>
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3z" />
    </>
  ),
  sparkle: <path d="M12 3v4M12 17v4M5 12H1M23 12h-4M6.3 6.3 9 9M15 15l2.7 2.7M17.7 6.3 15 9M9 15l-2.7 2.7" />,
  check: <path d="M5 12.5 10 17l9-10" />,
  // « Autres actions » : trois points verticaux (convention du menu contextuel).
  more: (
    <>
      <circle cx="12" cy="5" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="19" r="1.3" fill="currentColor" />
    </>
  ),
  // Actions des menus (renommer, déplacer, archiver, supprimer).
  pencil: <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />,
  move: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 13h6M13 11l2 2-2 2" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </>
  ),
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  refresh: (
    <>
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </>
  ),
  download: (
    <>
      <path d="M12 3v12M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </>
  ),
  filter: <path d="M3 5h18M6 12h12M10 19h4" />,
  group: (
    <>
      <rect x="3" y="4" width="18" height="6" rx="1.5" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" />
    </>
  ),
  sort: <path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" />,
  table: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 10h18M9 10v10" />
    </>
  ),
  cards: (
    <>
      <rect x="3" y="4" width="8" height="7" rx="1.5" />
      <rect x="13" y="4" width="8" height="7" rx="1.5" />
      <rect x="3" y="13" width="8" height="7" rx="1.5" />
      <rect x="13" y="13" width="8" height="7" rx="1.5" />
    </>
  ),
  back: <path d="m15 18-6-6 6-6" />,
  columns: (
    <>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="11" rx="1" />
      <rect x="17" y="4" width="4" height="7" rx="1" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 4v16M15 4v16" />
    </>
  ),
  rows: <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />,
  // Explorateur du Business Graph (ADR-129) : des objets reliés.
  graph: (
    <>
      <circle cx="5" cy="12" r="2.5" />
      <circle cx="19" cy="6" r="2.5" />
      <circle cx="19" cy="18" r="2.5" />
      <path d="M7.3 11 16.7 7M7.3 13l9.4 4" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  fit: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  maximize: <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />,
  minimize: <path d="M20 10h-6V4M14 10l7-7M4 14h6v6M10 14l-7 7" />,
  'arrow-up': <path d="M12 19V5M6 11l6-6 6 6" />,
  // Éditeurs des studios (ADR-146 addendum (12)) : barre, panneaux, barre de sélection.
  undo: <path d="M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" />,
  redo: <path d="m15 14 5-5-5-5M20 9H10a6 6 0 0 0 0 12h3" />,
  history: <path d="M12 7v5l3 2M3.5 12a8.5 8.5 0 1 0 2.5-6M3 4v4h4" />,
  // Tablette debout, bouton en bas (Font Awesome `tablet-screen-button`) ; le paysage la tourne (`rotate`).
  tablet: (
    <>
      <rect x="4.5" y="2.5" width="15" height="19" rx="2.5" />
      <path d="M4.5 17.5h15M11.5 19.5h1" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  rotate: <path d="M3 12a9 9 0 0 1 15.5-6.2M21 4v5h-5M21 12a9 9 0 0 1-15.5 6.2M3 20v-5h5" />,
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  'arrow-down': <path d="M12 5v14M6 13l6 6 6-6" />,
  'corner-up': <path d="M9 14 4 9l5-5M4 9h9a7 7 0 0 1 7 7v4" />,
  play: <path d="M7 4v16l13-8z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Icônes de type déclarées par un Pack (ADR-129, object-definition 1.9.0) : noms génériques, dessin simple de repli.
  drumstick: (
    <>
      <path d="M14.5 4.5a5 5 0 0 1 5 5c0 3-3 5.5-6 5.5l-4.5 4.5" />
      <path d="M14.5 4.5c-3 0-5.5 3-5.5 6l-4.5 4.5" />
      <circle cx="5" cy="19" r="1.8" />
    </>
  ),
  boxes: (
    <>
      <rect x="3" y="13" width="8" height="7" rx="1" />
      <rect x="13" y="13" width="8" height="7" rx="1" />
      <rect x="8" y="4" width="8" height="7" rx="1" />
    </>
  ),
  'truck-fast': (
    <>
      <path d="M7 6h9v10H7zM16 10h3l2 3v3h-5" />
      <circle cx="9.5" cy="18" r="1.6" />
      <circle cx="18" cy="18" r="1.6" />
      <path d="M2 9h3M3 12h2" />
    </>
  ),
  dolly: (
    <>
      <path d="M4 4h2l3 13h11" />
      <rect x="10" y="7" width="8" height="7" rx="1" />
      <circle cx="9" cy="19.5" r="1.5" />
    </>
  ),
  stethoscope: (
    <>
      <path d="M6 3v6a4 4 0 0 0 8 0V3" />
      <path d="M10 13v2a5 5 0 0 0 10 0v-2" />
      <circle cx="20" cy="11" r="2" />
    </>
  ),
  industry: <path d="M3 20V10l5 3V10l5 3V10l5 3V4h3v16z" />,
  'person-rifle': (
    <>
      <circle cx="10" cy="5" r="2" />
      <path d="M10 8v6M7 21l3-7 3 7M7 11h6M17 3v13M15 7h4" />
    </>
  ),
  // Formulaires 1.10.0 (ADR-146 addendum (16), point 8) : scanner, empreinte, appareil photo, flèche.
  scan: (
    <>
      <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
      <path d="M8 9h8M8 12h8M8 15h5" />
    </>
  ),
  paw: (
    <>
      <path d="M12 13c-2.8 0-5 2.4-5 4.4 0 1.4 1.1 2.1 2.4 2.1.9 0 1.7-.4 2.6-.4s1.7.4 2.6.4c1.3 0 2.4-.7 2.4-2.1 0-2-2.2-4.4-5-4.4z" />
      <ellipse cx="6" cy="10.5" rx="1.6" ry="2" />
      <ellipse cx="9.5" cy="6.5" rx="1.6" ry="2.1" />
      <ellipse cx="14.5" cy="6.5" rx="1.6" ry="2.1" />
      <ellipse cx="18" cy="10.5" rx="1.6" ry="2" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  circle: <circle cx="12" cy="12" r="8" />,
  // Éditeur v2 des formulaires (ADR-146 addendum (18)) : widgets génériques de la bibliothèque, langues, préréglages.
  'text-long': <path d="M4 6h16M4 10h16M4 14h16M4 18h10" />,
  stepper: (
    <>
      <rect x="2.5" y="7" width="19" height="10" rx="2" />
      <path d="M6 12h3M15 12h3M16.5 10.5v3M12 7v10" />
    </>
  ),
  slider: (
    <>
      <path d="M3 12h18" />
      <circle cx="9" cy="12" r="3" />
    </>
  ),
  star: <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />,
  tiles: (
    <>
      <rect x="3" y="4" width="8" height="7" rx="1.5" />
      <rect x="13" y="4" width="8" height="7" rx="1.5" />
      <rect x="3" y="13" width="8" height="7" rx="1.5" />
      <rect x="13" y="13" width="8" height="7" rx="1.5" />
    </>
  ),
  segmented: (
    <>
      <rect x="2.5" y="7" width="19" height="10" rx="2" />
      <path d="M9 7v10M15 7v10" />
    </>
  ),
  pointer: (
    <>
      <rect x="3" y="7" width="18" height="10" rx="5" />
      <path d="M8 12h8" />
    </>
  ),
  chip: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <circle cx="7.5" cy="12" r="1.5" />
    </>
  ),
  sigma: <path d="M18 5H6l6 7-6 7h12" />,
  'id-card': (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <circle cx="8.5" cy="11" r="2.2" />
      <path d="M5.5 16c.6-1.6 1.8-2.4 3-2.4s2.4.8 3 2.4M14 10h5M14 14h4" />
    </>
  ),
  translate: <path d="M4 5h9M8.5 3v2M6 5c1 3.5 3 6 6 7.5M11 5c-1 4-3.5 7-7 9M13 21l4-10 4 10M14.5 17h5" />,
  grip: <path d="M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01" />,
  'arrow-left': <path d="M19 12H5M11 6l-6 6 6 6" />,
  unlink: <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-2.4-6.8M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 2.4 6.8M3 3l18 18" />,
  layout: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M10.6 5.1A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.9" />
      <path d="M6.6 6.6C3.7 8.4 2 12 2 12s3.5 7 10 7c1.9 0 3.6-.6 5-1.5" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m3 3 18 18" />
    </>
  ),
  // Natures des champs des studios (ADR-146 addendum (15)) : à la place des glyphes « Aa », « 1,0 », « JJ ».
  text: <path d="M5 6V4h14v2M12 4v16M9 20h6" />,
  hash: <path d="M5 9h15M4 15h15M10 3 8 21M16 3l-2 18" />,
  toggle: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="6" />
      <circle cx="16" cy="12" r="3" />
    </>
  ),
  list: <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />,
  signature: <path d="M3 17c3-6 5-9 6-9s-1 8 1 8 3-5 4-5 0 4 2 4 3-2 5-2M3 21h18" />,
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </>
  ),
};

/** Noms connus du registre. */
export const ICON_NAMES: readonly string[] = Object.keys(PATHS);

export function Icon({ name, size = 20, title, rotate }: { name: string | undefined; size?: number; title?: string; /** Quart de tour (appareil en paysage) : `fa-rotate-90`, ou la même rotation sur le SVG de repli. */ rotate?: 90 }) {
  const known = name !== undefined && PATHS[name] !== undefined;
  const awesome = name === undefined ? undefined : FONT_AWESOME_NAMES[name];
  if (fontAwesomeEnabled() && awesome !== undefined) {
    return (
      <span
        className="ay-icon ay-icon--fa"
        style={{ width: size, height: size, fontSize: size * 0.85 }}
        aria-hidden={title === undefined ? true : undefined}
        role={title === undefined ? undefined : 'img'}
        aria-label={title}
        data-icon={name}
      >
        <i className={`fa-${FONT_AWESOME_STYLE} fa-${awesome}${rotate === 90 ? ' fa-rotate-90' : ''}`} />
      </span>
    );
  }
  return (
    <svg
      className="ay-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title === undefined ? true : undefined}
      role={title === undefined ? undefined : 'img'}
      data-icon={known ? name : 'generic'}
      {...(rotate === 90 ? { style: { transform: 'rotate(90deg)' } } : {})}
    >
      {title !== undefined && <title>{title}</title>}
      {known ? PATHS[name!] : <rect x="5" y="5" width="14" height="14" rx="3" />}
    </svg>
  );
}
